import { useState, PropsWithChildren } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import { withFormStyles, WithFormStyles } from '../../../util/withFormStyles';
import { Form } from 'informed';
import { EmailInput } from '@calefy-inc/informedMaterial';
import { graphql } from '../../../gql';
import { useMutation } from '@apollo/client';
import LoadingButton from '@mui/lab/LoadingButton';
import { styled } from '@mui/material';
import { PolicyStreamContactBroker } from './PolicyStreamContactBroker';
import { ErrorP } from '../../common/Typography';
import { SupportLink } from '../../common/SupportLink';
import { PolicyStreamButton } from './PolicyStreamButton';

interface PolicyStreamSendResumeEmailButtonProps
  extends PropsWithChildren<{}> { }
export const PolicyStreamSendResumeEmailButton = ({
  children,
}: PolicyStreamSendResumeEmailButtonProps) => {
  const [open, setOpen] = useState<boolean>(false);
  return (
    <>
      <PolicyStreamButton
        variant='contained'
        onClick={() => setOpen(true)}
        sx={{ borderRadius: (theme) => theme.spacing(2), minWidth: '250px' }}
      >
        {children}
      </PolicyStreamButton>
      <SendResumeEmailDialog
        open={open}
        setOpen={(open: boolean) => setOpen(open)}
        onCancel={() => setOpen(false)}
      />
    </>
  );
};

interface SendResumeEmailDialogProps extends WithFormStyles {
  open: boolean;
  setOpen: (open: boolean) => void;
  onCancel: () => void;
}
const SendResumeEmailDialog = withFormStyles()(
  ({ classes, open, setOpen, onCancel }: SendResumeEmailDialogProps) => {
    const [sendEmail, { loading, error, data, reset }] = useMutation(
      SEND_POLICYSTREAM_RESUME_EMAIL,
    );
    return (
      <Dialog open={open}>
        <DialogTitle>Access Application</DialogTitle>
        <Form
          className={classes.form}
          onSubmit={
            (formState) => {
              const { values } = formState;
              const email = String(values.email);
              sendEmail({ variables: { email } });
            }
          }
        >
          <DialogContent>
            <Typography>
              If you've already started an application, enter the email that was
              used when filling it out and we'll send an email to that address
              with a link to continue the application from where you left off!
            </Typography>
            <EmailInput
              disabled={data}
              className={classes.emailinput}
              name='email'
              label='Email'
              required={true}
            />
            {data?.sendPolicystreamResumeEmail?.email ? (
              <EmailSuccessContainer>
                <Typography>
                  Great! If there's an incomplete application belonging to{' '}
                  <a href={`mailto:${data.sendPolicystreamResumeEmail.email}`}>
                    {data.sendPolicystreamResumeEmail.email}
                  </a>
                  , we'll send a message to them with a link to resume the
                  application process.
                </Typography>
                <Typography>If you didn't receive an email:</Typography>
                <ul>
                  <li>
                    <Typography>
                      Please ensure that enough time has passed; it can
                      sometimes take up to 5 minutes for the email to arrive
                    </Typography>
                  </li>
                  <li>
                    <Typography>Check your spam folder</Typography>
                  </li>
                  <li>
                    <Typography>
                      Ensure that there are no typos in the email address
                    </Typography>
                  </li>
                  <li>
                    <Typography>
                      It could be that the application has already been
                      completed; in this case, no email will be sent
                    </Typography>
                  </li>
                </ul>
                <Typography>
                  If you have not received an email and believe that you should
                  have, please contact your broker directly{' '}
                  <PolicyStreamContactBroker />.
                </Typography>
              </EmailSuccessContainer>
            ) : null}
            {error ? (
              <EmailErrorContainer>
                <ErrorP>Oops! Something went wrong!</ErrorP>
                <Typography>
                  Please try refreshing the page and trying again. If the error
                  persist, please get in touch with our dedicated support team
                  at{' '}
                  <SupportLink
                    subject={`Error resuming PolicyStream application: ${error.message}`}
                  />
                  .
                </Typography>
                <ErrorP>Error: {error.message}</ErrorP>
              </EmailErrorContainer>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button
              disabled={!!data}
              onClick={() => {
                reset();
                onCancel();
              }}
            >
              Cancel
            </Button>
            {data || error ? (
              <Button
                onClick={() => {
                  reset();
                  setOpen(false);
                }}
              >
                Done
              </Button>
            ) : (
              <LoadingButton loading={loading} type='submit'>
                Send Email
              </LoadingButton>
            )}
          </DialogActions>
        </Form>
      </Dialog>
    );
  },
);

const EmailSuccessContainer = styled('div')({});
const EmailErrorContainer = styled('div')({});

const SEND_POLICYSTREAM_RESUME_EMAIL = graphql(`
  mutation sendPolicystreamResumeEmail($email: String!) {
    sendPolicystreamResumeEmail(email: $email) {
      email
    }
  }
`);
